import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Info } from "lucide-react";
import { useLanguage } from "@/i18n";

const IMAGES = [
  { src: "https://images.unsplash.com/photo-1704789202340-85a4786aa2c0?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200", cat: "interiors", alt: "Camera matrimoniale" },
  { src: "https://images.unsplash.com/photo-1688646953306-5ec93eab8c06?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200", cat: "interiors", alt: "Soggiorno" },
  { src: "https://images.unsplash.com/photo-1737233459465-8eaf6c7d8856?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200", cat: "interiors", alt: "Cucina abitabile" },
  { src: "https://images.pexels.com/photos/26859049/pexels-photo-26859049.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", cat: "interiors", alt: "Camera doppia" },
  { src: "https://images.unsplash.com/photo-1756079664354-34944e001f6d?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200", cat: "interiors", alt: "Bagno" },
  { src: "https://images.unsplash.com/photo-1763506425614-cdfee3349eff?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200", cat: "balconies", alt: "Balcone" },
  { src: "https://images.unsplash.com/photo-1558652361-3d3b72a5dc79?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200", cat: "coast", alt: "Scogli" },
  { src: "https://images.unsplash.com/photo-1558436593-d7e0ffa49397?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200", cat: "coast", alt: "Costa" },
  { src: "https://images.pexels.com/photos/3649209/pexels-photo-3649209.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", cat: "coast", alt: "Dintorni" },
];

const Gallery = () => {
  const { t } = useLanguage();
  const [tab, setTab] = useState("all");
  const [lightbox, setLightbox] = useState(null);

  const tabs = [
    { key: "all", label: t.gallery.tabs.all },
    { key: "interiors", label: t.gallery.tabs.interiors },
    { key: "balconies", label: t.gallery.tabs.balconies },
    { key: "coast", label: t.gallery.tabs.coast },
  ];
  const visible = tab === "all" ? IMAGES : IMAGES.filter((i) => i.cat === tab);

  return (
    <section id="galleria" data-testid="gallery-section" className="py-20 lg:py-32 px-6 sm:px-12 lg:px-20">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-12"
        >
          <div>
            <p className="text-xs uppercase tracking-[0.25em] font-mono-alt font-semibold text-[#C86D51] mb-4">{t.gallery.eyebrow}</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#0D253A]">{t.gallery.title}</h2>
          </div>
          <div data-testid="gallery-tabs" className="flex flex-wrap gap-2">
            {tabs.map((tb) => (
              <button
                key={tb.key}
                data-testid={`gallery-tab-${tb.key}`}
                onClick={() => setTab(tb.key)}
                className={`px-4 py-2 text-xs font-semibold uppercase tracking-wider rounded-full border transition-colors duration-300 ${
                  tab === tb.key
                    ? "bg-[#0D253A] text-[#F8F5F0] border-[#0D253A]"
                    : "border-[#0D253A]/20 text-[#0D253A]/70 hover:border-[#C86D51] hover:text-[#C86D51]"
                }`}
              >
                {tb.label}
              </button>
            ))}
          </div>
        </motion.div>

        <motion.div layout className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-5">
          <AnimatePresence mode="popLayout">
            {visible.map((img, i) => (
              <motion.button
                layout
                key={img.src}
                data-testid={`gallery-item-${i}`}
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.45, delay: i * 0.05 }}
                onClick={() => setLightbox(img)}
                className={`overflow-hidden rounded-xl group ${i % 5 === 0 ? "row-span-2 h-full min-h-64" : "h-44 sm:h-56"}`}
              >
                <img src={img.src} alt={img.alt} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              </motion.button>
            ))}
          </AnimatePresence>
        </motion.div>

        <p data-testid="gallery-note" className="mt-6 flex items-center gap-2 text-xs text-[#5C6773]">
          <Info className="w-4 h-4 text-[#C86D51]" />
          {t.gallery.note}
        </p>
      </div>

      <AnimatePresence>
        {lightbox && (
          <motion.div
            data-testid="gallery-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setLightbox(null)}
            className="fixed inset-0 z-50 bg-[#0D253A]/92 backdrop-blur-sm flex items-center justify-center p-6"
          >
            <motion.img
              initial={{ scale: 0.92, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.92, y: 20 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              src={lightbox.src}
              alt={lightbox.alt}
              className="max-h-[85vh] max-w-full rounded-xl shadow-2xl"
            />
            <button
              data-testid="lightbox-close"
              onClick={() => setLightbox(null)}
              className="absolute top-6 right-6 p-3 rounded-full bg-white/10 text-white hover:bg-[#C86D51] transition-colors"
              aria-label="Chiudi"
            >
              <X className="w-5 h-5" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Gallery;
